<template>
  <Teleport to="body">
    <div
        v-if="isVisible"
        class="fixed left-4 top-1/2 transform -translate-y-1/2 z-50 hidden lg:block"
        :class="{ 'opacity-100': isVisible, 'opacity-0': !isVisible }"
    >
      <!-- Container du stepper -->
      <div class="relative">
        <!-- Ligne de fond (grise) -->
        <div class="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gray-300 rounded-full"></div>

        <!-- Ligne de progression (rouge) -->
        <div
            class="absolute left-1/2 transform -translate-x-1/2 w-0.5 bg-gradient-to-b from-red-500 to-red-600 rounded-full transition-all duration-300 ease-out"
            :style="{ height: `${overallProgress}%` }"
        ></div>

        <!-- Steps -->
        <div class="relative space-y-8">
          <div
              v-for="(step, index) in steps"
              :key="step.id"
              class="relative flex items-center group cursor-pointer"
              @click="scrollToSection(step.href)"
          >
            <!-- Point du step -->
            <div class="relative flex items-center justify-center">
              <!-- Cercle de fond -->
              <div
                  class="w-4 h-4 rounded-full border-2 transition-all duration-300"
                  :class="getStepClasses(index)"
              ></div>

              <!-- Point central animé -->
              <div
                  v-if="isStepActive(index) || isStepCompleted(index)"
                  class="absolute w-2 h-2 rounded-full transition-all duration-300"
                  :class="getStepDotClasses(index)"
              ></div>
            </div>

            <!-- Label du step (affiché au hover) -->
            <div
                class="absolute left-8 px-3 py-1 bg-gray-900 text-white text-sm rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-10"
                :class="{ 'transform translate-x-2': true }"
            >
              {{ $t(step.label) }}
              <!-- Flèche pointant vers le step -->
              <div class="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-0 h-0 border-t-2 border-b-2 border-r-4 border-transparent border-r-gray-900"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps({
  menu: {
    type: Array,
    required: true,
    validator: (menu) => {
      return menu.every(item => item.href && item.label)
    }
  },
  offset: {
    type: Number,
    default: 80 // Hauteur de la navbar
  }
})

// État réactif
const currentStepIndex = ref(0)
const currentStepProgress = ref(0)
const isVisible = ref(false)

// Filtrer seulement les éléments avec des ancres (#)
const steps = computed(() => {
  return props.menu
      .filter(item => item.href.startsWith('#'))
      .map(item => ({
        id: item.href.replace('#', ''),
        href: item.href,
        label: item.label
      }))
})

// Version hybride - combine les deux approches
const overallProgress = computed(() => {
  if (steps.value.length === 0) return 0

  try {
    // Essayer d'abord la méthode basée sur les distances
    const sectionHeights = steps.value.map(step => {
      const element = document.getElementById(step.id)
      return element ? element.offsetHeight : 0
    })

    const totalHeight = sectionHeights.reduce((sum, height) => sum + height, 0)

    if (totalHeight > 0) {
      // Calculer le progrès pondéré par hauteur
      let weightedProgress = 0

      // Sections complètement traversées
      for (let i = 0; i < currentStepIndex.value; i++) {
        weightedProgress += sectionHeights[i] || 0
      }

      // Section courante
      if (currentStepIndex.value < sectionHeights.length) {
        const currentHeight = sectionHeights[currentStepIndex.value] || 0
        weightedProgress += currentHeight * currentStepProgress.value
      }

      return (weightedProgress / totalHeight) * 100
    }
  } catch (error) {
    console.warn('Erreur dans le calcul du progrès:', error)
  }

  // Fallback sur la méthode simple
  const totalSteps = steps.value.length
  return ((currentStepIndex.value + currentStepProgress.value) / totalSteps) * 100
})

// Classes pour les steps
const getStepClasses = (index) => {
  if (isStepCompleted(index)) {
    return 'border-red-500 bg-red-500 scale-110'
  } else if (isStepActive(index)) {
    return 'border-red-500 bg-white scale-105'
  } else {
    return 'border-gray-300 bg-white hover:border-red-300 hover:scale-105'
  }
}

const getStepDotClasses = (index) => {
  if (isStepCompleted(index)) {
    return 'bg-white'
  } else if (isStepActive(index)) {
    return 'bg-red-500'
  }
  return ''
}

const isStepCompleted = (index) => {
  return index < currentStepIndex.value
}

const isStepActive = (index) => {
  return index === currentStepIndex.value
}

// Navigation vers une section
const scrollToSection = (anchor) => {
  const targetId = anchor.replace('#', '')
  const element = document.getElementById(targetId)

  if (element) {
    const elementPosition = element.offsetTop - props.offset

    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth'
    })
  }
}

// Gestion du scroll
// Version plus réactive - remplacez toute la fonction handleScroll
const handleScroll = () => {
  const scrollTop = window.scrollY || document.documentElement.scrollTop
  const windowHeight = window.innerHeight
  const documentHeight = document.documentElement.scrollHeight

  isVisible.value = scrollTop > 100

  if (steps.value.length === 0) return

  let activeIndex = 0
  let progress = 0

  // Approche plus fluide basée sur la position relative
  for (let i = 0; i < steps.value.length; i++) {
    const element = document.getElementById(steps.value[i].id)
    if (!element) continue

    const elementTop = element.offsetTop - props.offset
    const elementBottom = elementTop + element.offsetHeight

    // Zone d'activation étendue (commence 20% avant la section)
    const activationZone = element.offsetHeight * 0.2
    const extendedTop = elementTop - activationZone

    if (scrollTop >= extendedTop - 50) { // -50px pour encore plus d'anticipation
      activeIndex = i

      if (scrollTop < elementBottom) {
        // Calcul du progrès avec zone étendue
        const totalZone = element.offsetHeight + activationZone
        const relativeScroll = scrollTop - extendedTop + 50
        progress = Math.min(Math.max(relativeScroll / totalZone, 0), 1)
      } else {
        progress = 1
      }
    }
  }

  // Fin de page
  if (scrollTop + windowHeight >= documentHeight - 10) {
    activeIndex = steps.value.length - 1
    progress = 1
  }

  currentStepIndex.value = activeIndex
  currentStepProgress.value = progress
}

// Version throttlée du scroll pour les performances
let scrollTimeout = null
const throttledHandleScroll = () => {
  if (scrollTimeout) return

  scrollTimeout = setTimeout(() => {
    handleScroll()
    scrollTimeout = null
  }, 16) // ~60fps
}

// Hooks de cycle de vie
onMounted(async () => {
  await nextTick()
  window.addEventListener('scroll', throttledHandleScroll, {passive: true})
  // Initialiser la position
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', throttledHandleScroll)
  if (scrollTimeout) {
    clearTimeout(scrollTimeout)
  }
})
</script>

<style scoped>
/* Animation de pulsation pour le step actif */
@keyframes pulse-red {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0);
  }
}

.group:hover .w-4.h-4.border-red-500 {
  animation: pulse-red 1.5s infinite;
}

/* Transition fluide pour le tooltip */
.group:hover div[class*="opacity-0"] {
  transform: translateX(8px);
}
</style>